/**
 * Learning-path badges — tutorial / progress only (no token rewards).
 */

export type BadgeId =
	| "connect_ii"
	| "create_vault"
	| "mint_usdm"
	| "liquidate_vault"
	| "agent_type_f"
	| "agent_type_a";

export type BadgeDef = {
	id: BadgeId;
	order: number;
	title: string;
	description: string;
	howTo: string;
	to: string;
};

export const BADGE_ORDER: BadgeId[] = [
	"connect_ii",
	"create_vault",
	"mint_usdm",
	"liquidate_vault",
	"agent_type_f",
	"agent_type_a",
];

export const BADGE_CATALOG: Record<BadgeId, BadgeDef> = {
	connect_ii: {
		id: "connect_ii",
		order: 1,
		title: "Identity linked",
		description: "Connect Internet Identity to the app.",
		howTo: "Tap Connect in the top bar.",
		to: "/profile",
	},
	create_vault: {
		id: "create_vault",
		order: 2,
		title: "Vault opener",
		description: "Open your first CDP vault.",
		howTo: "Go to Borrow and create a vault.",
		to: "/borrow",
	},
	mint_usdm: {
		id: "mint_usdm",
		order: 3,
		title: "USDM minter",
		description: "Mint Motoko Dollar against collateral.",
		howTo: "Deposit collateral, then mint USDM.",
		to: "/borrow",
	},
	liquidate_vault: {
		id: "liquidate_vault",
		order: 4,
		title: "Keeper",
		description: "Browse or execute a liquidation.",
		howTo: "Open Liquidate and review at-risk vaults.",
		to: "/liquidation",
	},
	agent_type_f: {
		id: "agent_type_f",
		order: 5,
		title: "Fast Agent",
		description: "Register a Type F agent on the gateway.",
		howTo: "Enter Agent Layer → Type F → Register.",
		to: "/agents",
	},
	agent_type_a: {
		id: "agent_type_a",
		order: 6,
		title: "API Agent",
		description: "Create an API key for bots.",
		howTo: "Agent Layer → Type A → Create API key.",
		to: "/agents#type-a",
	},
};

const PREFIX = "motokomaker.v2.badges.";

export function loadLocalBadges(principal: string): Set<BadgeId> {
	if (!principal || typeof localStorage === "undefined") return new Set();
	try {
		const raw = localStorage.getItem(PREFIX + principal);
		if (!raw) return new Set();
		const arr = JSON.parse(raw) as string[];
		return new Set(arr.filter((x): x is BadgeId => x in BADGE_CATALOG));
	} catch {
		return new Set();
	}
}

export function markLocalBadges(principal: string, ids: BadgeId[]) {
	if (!principal || typeof localStorage === "undefined") return;
	const set = loadLocalBadges(principal);
	for (const id of ids) set.add(id);
	localStorage.setItem(PREFIX + principal, JSON.stringify([...set]));
}

export function earnedCount(set: Set<BadgeId>): number {
	return BADGE_ORDER.filter((id) => set.has(id)).length;
}

export function nextBadgeId(set: Set<BadgeId>): BadgeId | null {
	for (const id of BADGE_ORDER) {
		if (!set.has(id)) return id;
	}
	return null;
}
